import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Save, RotateCcw, Loader2, FileText, Cpu, Zap, Crown, Code } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import axios from 'axios';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PROMPT_TYPE_INFO = {
  functional: {
    icon: Cpu,
    label: "Nivel 1 - Funcional",
    description: "Lectura y presentación de datos. LEER, no calcular.",
    color: "bg-blue-500",
    level: 1
  },
  premium: {
    icon: Zap,
    label: "Nivel 2 - Premium",
    description: "Diagnóstico y proyecciones tácticas. Narrativa ejecutiva.",
    color: "bg-amber-500",
    level: 2
  },
  c_level: {
    icon: Crown,
    label: "Nivel 3 - C-Level",
    description: "Estrategia ejecutiva. 7 elementos obligatorios.",
    color: "bg-red-500",
    level: 3
  },
  default_agent: {
    icon: Code,
    label: "Prompt por Defecto",
    description: "Se usa cuando un agente no tiene prompt personalizado.",
    color: "bg-slate-500",
    level: 0
  }
};

const InternalPromptsConfig = () => {
  const queryClient = useQueryClient();
  const [prompts, setPrompts] = useState({});
  const [activeType, setActiveType] = useState('functional');
  const [hasChanges, setHasChanges] = useState(false);

  // Fetch existing prompts
  const { data: existingPrompts, isLoading } = useQuery({
    queryKey: ['internal-prompts'],
    queryFn: async () => {
      const response = await axios.get(`${API}/admin/internal-prompts`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      return response.data;
    }
  });

  // Initialize prompts when data loads
  useEffect(() => {
    if (existingPrompts?.prompts) {
      setPrompts(existingPrompts.prompts);
      setHasChanges(false);
    }
  }, [existingPrompts]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async (promptsData) => {
      const response = await axios.put(
        `${API}/admin/internal-prompts`,
        { prompts: promptsData },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      return response.data;
    },
    onSuccess: () => {
      toast.success('Prompts internos guardados correctamente');
      queryClient.invalidateQueries(['internal-prompts']);
      setHasChanges(false);
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Error al guardar prompts');
    }
  });

  // Reset mutation
  const resetMutation = useMutation({
    mutationFn: async () => {
      const response = await axios.post(
        `${API}/admin/internal-prompts/reset`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      return response.data;
    },
    onSuccess: () => {
      toast.success('Prompts restaurados a valores por defecto');
      queryClient.invalidateQueries(['internal-prompts']);
      setHasChanges(false);
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Error al restaurar prompts');
    }
  });

  const updatePrompt = (type, value) => {
    setPrompts(prev => ({
      ...prev,
      [type]: value
    }));
    setHasChanges(true);
  };

  const handleSave = () => {
    saveMutation.mutate(prompts);
  };

  const handleReset = () => {
    if (window.confirm('¿Estás seguro de restaurar TODOS los prompts a valores por defecto? Esta acción no se puede deshacer.')) {
      resetMutation.mutate();
    }
  };

  const countWords = (text) => {
    return text ? text.trim().split(/\s+/).filter(w => w.length > 0).length : 0;
  };

  const countLines = (text) => {
    return text ? text.split('\n').length : 0;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  const TypeInfo = PROMPT_TYPE_INFO[activeType];
  const TypeIcon = TypeInfo?.icon || FileText;

  return (
    <div className="space-y-6">
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg">
                <FileText className="w-6 h-6 text-white" />
              </div>
              <div>
                <CardTitle className="text-white">Prompts Internos de Agentes</CardTitle>
                <CardDescription className="text-slate-400">
                  Define los prompts de sistema para cada nivel de agente (Funcional, Premium, C-Level)
                </CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {existingPrompts?.is_default && (
                <Badge variant="outline" className="border-green-500 text-green-400">
                  Usando Defaults
                </Badge>
              )}
              {hasChanges && (
                <Badge variant="outline" className="border-yellow-500 text-yellow-400">
                  Cambios sin guardar
                </Badge>
              )}
              <Button
                variant="outline"
                onClick={handleReset}
                disabled={resetMutation.isPending}
                className="border-slate-600 text-slate-300 hover:bg-slate-700"
              >
                {resetMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <RotateCcw className="w-4 h-4 mr-2" />
                )}
                Restaurar Defaults
              </Button>
              <Button
                onClick={handleSave}
                disabled={saveMutation.isPending || !hasChanges}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {saveMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Guardar Cambios
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs value={activeType} onValueChange={setActiveType}>
            <TabsList className="grid grid-cols-4 bg-slate-900/50 mb-6">
              {Object.entries(PROMPT_TYPE_INFO).map(([type, info]) => {
                const Icon = info.icon;
                return (
                  <TabsTrigger
                    key={type}
                    value={type}
                    className="flex items-center gap-2 data-[state=active]:bg-slate-700"
                  >
                    <Icon className="w-4 h-4" />
                    <span className="hidden lg:inline">{info.label}</span>
                    <span className="lg:hidden">Nivel {info.level || 'D'}</span>
                  </TabsTrigger>
                );
              })}
            </TabsList>

            {Object.keys(PROMPT_TYPE_INFO).map((type) => {
              const typeInfo = PROMPT_TYPE_INFO[type];
              const promptText = prompts[type] || '';
              const Icon = typeInfo.icon;
              
              return (
                <TabsContent key={type} value={type} className="space-y-4">
                  {/* Type Description */}
                  <div className={`p-4 rounded-lg bg-slate-900/50 border border-slate-700`}>
                    <div className="flex items-center gap-3 mb-2">
                      <div className={`p-2 rounded-lg ${typeInfo.color}`}>
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <Badge className={typeInfo.color}>{typeInfo.label}</Badge>
                        <p className="text-slate-400 text-sm mt-1">{typeInfo.description}</p>
                      </div>
                    </div>
                  </div>

                  {/* Prompt Editor */}
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <label className="text-slate-300 font-medium">Prompt de Sistema</label>
                      <div className="flex gap-4 text-xs text-slate-500">
                        <span>{countWords(promptText)} palabras</span>
                        <span>{countLines(promptText)} líneas</span>
                        <span>{promptText.length} caracteres</span>
                      </div>
                    </div>
                    <Textarea
                      value={promptText}
                      onChange={(e) => updatePrompt(type, e.target.value)}
                      className="bg-slate-900 border-slate-600 text-white min-h-[500px] font-mono text-sm leading-relaxed"
                      placeholder={`Define el prompt interno para agentes de tipo ${typeInfo.label}...`}
                    />
                    <p className="text-xs text-slate-500">
                      Este prompt se inyecta automáticamente en todos los agentes de tipo <strong>{typeInfo.label}</strong>.
                      Los cambios se aplicarán a las próximas ejecuciones de agentes.
                    </p>
                  </div>
                </TabsContent>
              );
            })}
          </Tabs>
        </CardContent>
      </Card>

      {/* Prompt Summary Cards */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white text-lg">Resumen de Prompts por Nivel</CardTitle>
          <CardDescription className="text-slate-400">
            Vista rápida del tamaño y estado de cada prompt interno
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-4 gap-4">
            {Object.entries(prompts).map(([type, promptText]) => {
              const typeInfo = PROMPT_TYPE_INFO[type];
              if (!typeInfo) return null;
              const Icon = typeInfo.icon;
              const words = countWords(promptText);
              const lines = countLines(promptText);
              
              return (
                <div 
                  key={type} 
                  className={`p-4 bg-slate-900/50 rounded-lg border border-slate-700 hover:border-slate-600 transition-colors cursor-pointer ${activeType === type ? 'ring-2 ring-purple-500' : ''}`}
                  onClick={() => setActiveType(type)}
                >
                  <div className="flex items-center gap-2 mb-3">
                    <div className={`p-1.5 rounded ${typeInfo.color}`}>
                      <Icon className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-white font-medium text-sm">{typeInfo.label}</span>
                  </div>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Palabras:</span>
                      <span className="text-slate-300 font-mono">{words.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Líneas:</span>
                      <span className="text-slate-300 font-mono">{lines}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Caracteres:</span>
                      <span className="text-slate-300 font-mono">{promptText?.length?.toLocaleString() || 0}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Last Update Info */}
      {existingPrompts?.updated_at && (
        <div className="text-center text-sm text-slate-500">
          Última actualización: {new Date(existingPrompts.updated_at).toLocaleString()} 
          {existingPrompts.updated_by && ` por ${existingPrompts.updated_by}`}
        </div>
      )}
    </div>
  );
};

export default InternalPromptsConfig;
